import { Grid } from "@mui/material";
import { InputItem } from "pepsico-ds";
import { useContext, useMemo, useState } from "react";
import EmptyImage from "../../assets/widget/create_new_widget_empty.svg";
import ButtonGroupDynamic from "../../components/common/ButtonGroupDynamic";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import {
  ConfigurationDrawer,
  staticConfigs,
} from "../../components/management/ConfigurationDrawer";
import SystemSettingCard from "../../components/management/SystemSettingCard/SystemSettingCard";
import SystemSettingTable from "../../components/management/SystemSettingTable/SystemSettingTable";
import CreateRewardsModal from "../../components/rewards/CreateRewardsModal/CreateRewardsModal";
import { AppConfigContext } from "../../context/AppConfigContext";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import { useHasAccessPermission } from "../../hooks/useHasAccessPermission";
import "./../../styles/common.scss";
import "./ManagementPage.scss";

const LIST_VIEW_TYPES = [
  {
    icon: "view_module",
    viewType: "grid",
  },
  {
    icon: "view_list",
    viewType: "list",
  },
];

const ALWAYS_VISIBLE_STATIC_CONFIGS = new Set(["fraud_abuse_controls"]);
const APP_CONFIG_GATED_STATIC_CONFIGS = new Set([
  "points_conversion_rate",
  "points_lifecycle_settings",
]);

export default function SystemSettingPage() {
  const {
    globalState: { programConfig },
  } = useContext(GlobalConfigContext);
  const { appConfig } = useContext(AppConfigContext) || {};
  const [createRewardModalOpen, setCreateRewardModalOpen] = useState(false);
  const [viewType, setViewType] = useState("grid");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerConfigKey, setDrawerConfigKey] = useState("");
  const [drawerMode, setDrawerMode] = useState("view");
  const hasAccess = useHasAccessPermission();
  const hasSystemSettingsAccess =
    hasAccess("navigation.management_keywords") ||
    hasAccess("navigation.system_settings");
  const canConfigureProgramSettings = hasAccess(
    "permissions.configure_program_settings"
  );
  const [inputSearchText, setInputSearchText] = useState("");
  const [toast, setToast] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
  });

  const configData = useMemo(() => {
    const systemSettingsList = appConfig?.systemSettings?.list || [];
    const hasConfiguredSystemSettings = systemSettingsList.length > 0;
    const systemSettingsSet = new Set(systemSettingsList);
    const apiConfigs =
      programConfig?.configurations
        ?.filter(
          (config) =>
            !hasConfiguredSystemSettings || systemSettingsSet.has(config.key)
        )
        ?.map((config) => ({
          id: config.key,
          name: config?.value?.title || "",
          description: config?.value?.description || "",
          data: { ...config?.value?.data },
        }))
        ?.filter((config) => config?.name) || [];

    const staticEntries = Object.entries(staticConfigs)
      .filter(([key]) => {
        if (ALWAYS_VISIBLE_STATIC_CONFIGS.has(key)) return true;
        if (APP_CONFIG_GATED_STATIC_CONFIGS.has(key)) {
          return systemSettingsSet.has(key);
        }
        return !hasConfiguredSystemSettings || systemSettingsSet.has(key);
      })
      .map(([key, config]) => ({
        id: key,
        name: config.title,
        description: config.description,
        data: {},
      }));

    return [...apiConfigs, ...staticEntries];
  }, [appConfig, programConfig]);

  const filteredData = useMemo(
    () =>
      configData.filter((config) => {
        return (
          (config.name.toLowerCase().includes(inputSearchText.toLowerCase()) ||
            config.description
              .toLowerCase()
              .includes(inputSearchText.toLocaleLowerCase())) &&
          config?.name
        );
      }),
    [configData, inputSearchText]
  );

  const openDrawer = (id, mode) => {
    setDrawerConfigKey(id);
    setDrawerMode(mode);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerConfigKey("");
  };

  const onClickEdit = (id) => {
    openDrawer(id, "edit");
  };

  const onClickView = (id) => {
    openDrawer(id, "view");
  };

  const toggleModalNewReward = () => {
    setCreateRewardModalOpen(!createRewardModalOpen);
  };

  const handleInputSearchText = (event) => {
    const { value } = event.target;
    setInputSearchText(value);
  };

  return (
    <div className="container system-settings-page">
      <div className="wrap">
        {createRewardModalOpen && (
          <CreateRewardsModal
            createRewardModalOpen={createRewardModalOpen}
            toggleModalNewReward={toggleModalNewReward}
          />
        )}
        <div className="heading-wrap">
          <h2>Program Settings</h2>
          <div className="filter-container">
            <div className="filter-container-search">
              <InputItem
                placeholder="Type a search keyword..."
                trailingIcon="search"
                size="medium"
                className="input-search"
                onChange={handleInputSearchText}
              />
            </div>
            <div className="filter-button-group">
              <ButtonGroupDynamic
                items={LIST_VIEW_TYPES.map((item) => ({
                  ...item,
                  onClick: () => setViewType(item.viewType),
                }))}
                size="medium"
                spacing="2px"
                variant="toggle"
                selectedIndex={viewType === "grid" ? 0 : 1}
              />
            </div>
          </div>
        </div>

        <div className="card-list">
          {filteredData?.length > 0 ? (
            <>
              {viewType === "grid" ? (
                <Grid container spacing={2}>
                  {filteredData.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <SystemSettingCard
                        {...item}
                        onClickView={onClickView}
                        onClickEdit={onClickEdit}
                        hasEditAccess={canConfigureProgramSettings}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <SystemSettingTable
                  rewardData={filteredData}
                  onClickView={onClickView}
                  onClickEdit={onClickEdit}
                  hasEditAccess={canConfigureProgramSettings}
                  canConfigureProgramSettings={canConfigureProgramSettings}
                />
              )}
            </>
          ) : (
            <section className="empty-state-container">
              <section className="empty-state">
                <img src={EmptyImage} alt="No settings was found" />
                <p>No settings was found</p>
              </section>
            </section>
          )}
        </div>
      </div>
      <ConfigurationDrawer
        canEdit={
          drawerConfigKey === "partner_configuration"
            ? canConfigureProgramSettings
            : hasSystemSettingsAccess
        }
        open={drawerOpen}
        onClose={closeDrawer}
        onEdit={() => setDrawerMode("edit")}
        configKey={drawerConfigKey}
        mode={drawerMode}
      />
      {toast && (
        <ToastNotification
          {...toast}
          handleClose={() => setToast({ ...toast, open: false })}
        />
      )}
    </div>
  );
}
